/* Homepage effects: no external libraries; stop work offscreen and on hidden tabs. */
(() => {
  if (window.HbnMagicHomepage) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const warm = ['#c99651', '#a482e0', '#dd906d', '#73c5b1', '#ffffff'];

  class MagicSurface {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      // The storefront has one fixed particle layer. Local roots keep their CTA/tilt effects.
      const localSurface = document.body.classList.contains('hbn-site') && !root.classList.contains('hbn-site-backdrop');
      this.canvases = [...(localSurface ? [] : root.querySelectorAll('[data-hbn-particles]'))].map(canvas => ({ canvas, ctx: canvas.getContext('2d'), parts: [], w: 0, h: 0 })).filter(f => f.ctx);
      this.visible = false;
      this.tick = this.tick.bind(this);
      this.refresh = this.refresh.bind(this);
      this.resize = this.resize.bind(this);
      document.addEventListener('visibilitychange', this.refresh, { signal: this.abort.signal });
      reduced.addEventListener('change', this.refresh, { signal: this.abort.signal });
      this.resize();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.resize);
        this.canvases.forEach(f => this.resizeObserver.observe(f.canvas));
      } else window.addEventListener('resize', this.resize, { signal: this.abort.signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.refresh();
          if (this.visible) this.reveal();
        }, { threshold: 0 });
        this.observer.observe(root);
      } else { this.visible = true; this.refresh(); this.reveal(); }
      this.scene = root.querySelector('[data-hbn-tilt]');
      if (this.scene) {
        this.root.addEventListener('pointermove', event => {
          if (reduced.matches || !this.visible || event.pointerType !== 'mouse') return;
          const rect = this.scene.getBoundingClientRect();
          const x = Math.max(-1, Math.min(1, (event.clientX - rect.left - rect.width / 2) / (rect.width / 2)));
          const y = Math.max(-1, Math.min(1, (event.clientY - rect.top - rect.height / 2) / (rect.height / 2)));
          this.pointer = { x, y };
          if (!this.tiltFrame) this.tiltFrame = requestAnimationFrame(() => {
            this.tiltFrame = 0;
            this.scene.style.setProperty('--hbn-tilt-x', `${(this.pointer.x * 8).toFixed(2)}deg`);
            this.scene.style.setProperty('--hbn-tilt-y', `${(-this.pointer.y * 5.2).toFixed(2)}deg`);
            this.scene.style.setProperty('--hbn-light-x', `${50 + this.pointer.x * 35}%`);
            this.scene.style.setProperty('--hbn-light-y', `${50 + this.pointer.y * 35}%`);
          });
        }, { passive: true, signal: this.abort.signal });
        this.root.addEventListener('pointerleave', () => this.resetTilt(), { signal: this.abort.signal });
      }
    }
    resetTilt() {
      cancelAnimationFrame(this.tiltFrame); this.tiltFrame = 0;
      this.scene?.style.removeProperty('--hbn-tilt-x');
      this.scene?.style.removeProperty('--hbn-tilt-y');
      this.scene?.style.removeProperty('--hbn-light-x');
      this.scene?.style.removeProperty('--hbn-light-y');
    }
    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      if (reduced.matches) return;
      this.reveals = [...this.root.querySelectorAll('[data-hbn-reveal-item]')].map((item, index) => item.animate?.(
        [{ opacity: .35, transform: 'translateY(22px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 650, delay: index * 90, easing: 'cubic-bezier(.2,.7,.3,1)' }
      ));
    }
    resize() {
      const dpr = Math.min(devicePixelRatio || 1, 2);
      this.canvases.forEach(f => {
        const w = f.canvas.clientWidth, h = f.canvas.clientHeight;
        if (!w || !h || (f.w === w && f.h === h && f.dpr === dpr)) return;
        Object.assign(f, { w, h, dpr });
        f.canvas.width = Math.round(w * dpr); f.canvas.height = Math.round(h * dpr);
        f.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const colors = f.canvas.dataset.hbnParticles === 'light' ? ['#fffdf8', '#ffe1b4', '#dfccff'] : warm;
        const count = Math.min(w < 600 ? 22 : 48, Math.max(12, Math.round(w * h / 20000)));
        f.parts = Array.from({ length: count }, () => ({
          x: Math.random() * w,
          y: Math.random() * h,
          // Original crisp particles, exactly 25% larger; no shadow or glow.
          r: (1 + Math.random() * 2.3) * 1.25,
          speed: 6 + Math.random() * 15,
          phase: Math.random() * Math.PI * 2,
          waveRate: .3 + Math.random() * .6,
          waveAmount: .18 + Math.random() * .24,
          sway: 8 + Math.random() * 16,
          fadeRate: .38 + Math.random() * .62,
          fadePhase: Math.random() * Math.PI * 2,
          opacity: .45 + Math.random() * .27,
          color: colors[Math.floor(Math.random() * colors.length)],
          star: Math.random() > .55
        }));
        this.draw(f, 0, 0);
      });
    }
    draw(f, delta, time) {
      const { ctx, w, h } = f;
      ctx.clearRect(0, 0, w, h);
      ctx.shadowBlur = 0;
      for (const p of f.parts) {
        const wave = Math.sin(time * p.waveRate + p.phase);
        p.y -= p.speed * (1 + wave * p.waveAmount) * delta;
        if (p.y < -12) p.y = h + 12;
        const x = p.x + wave * p.sway;
        const fade = (Math.sin(time * p.fadeRate + p.fadePhase) + 1) / 2;
        const edgeFade = Math.max(0, Math.min(1, p.y / 24, (h - p.y) / 24));
        ctx.globalAlpha = Math.pow(fade, 1.6) * p.opacity * edgeFade;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        if (p.star) {
          for (let i = 0; i < 8; i++) {
            const a = i * Math.PI / 4 + p.phase, r = i % 2 ? p.r * .75 : p.r * 2.3;
            const px = x + Math.cos(a) * r, py = p.y + Math.sin(a) * r;
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          }
          ctx.closePath();
        } else ctx.arc(x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
    }
    tick(now) {
      this.frame = 0;
      if (!this.running) return;
      if (!this.last || now - this.last >= 32) {
        const delta = this.last ? Math.min((now - this.last) / 1000, .08) : 0;
        this.last = now;
        this.elapsed = (this.elapsed || 0) + delta;
        this.canvases.forEach(f => this.draw(f, delta, this.elapsed));
      }
      this.frame = requestAnimationFrame(this.tick);
    }
    refresh() {
      this.running = this.visible && !document.hidden && !reduced.matches;
      this.root.toggleAttribute('data-hbn-paused', !this.running);
      if (!this.running) { cancelAnimationFrame(this.frame); this.frame = 0; this.last = 0; this.resetTilt(); }
      else if (!this.frame && this.canvases.length) this.frame = requestAnimationFrame(this.tick);
      if (reduced.matches) { this.resetTilt(); this.reveals?.forEach(a => a?.cancel()); }
    }
    destroy() {
      this.running = false; cancelAnimationFrame(this.frame);
      this.abort.abort(); this.observer?.disconnect(); this.resizeObserver?.disconnect();
      this.reveals?.forEach(a => a?.cancel()); this.resetTilt();
    }
  }

  class PreviewDeck extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.cards = [...this.querySelectorAll('[data-hbn-card]')];
      if (this.cards.length !== 2) return;
      this.active = 0; this.busy = false; this.animations = [];
      this.cards.forEach(card => {
        const button = card.querySelector('[data-hbn-card-trigger]');
        button.hidden = false;
        button.addEventListener('click', () => this.swap(), { signal: this.abort.signal });
      });
      this.querySelector('[data-hbn-swap]').addEventListener('click', () => this.swap(), { signal: this.abort.signal });
      reduced.addEventListener('change', () => { if (reduced.matches) this.animations.forEach(a => a.finish()); }, { signal: this.abort.signal });
      this.querySelector('.hbn-preview-process__controls').hidden = false;
      this.classList.add('is-ready');
      this.update();
    }
    update() {
      this.cards.forEach((card, i) => {
        const isFront = i === this.active;
        card.classList.toggle('is-front', isFront); card.classList.toggle('is-back', !isFront);
        card.querySelector('.hbn-preview-process__card-body').setAttribute('aria-hidden', String(!isFront));
        card.querySelector('[data-hbn-card-trigger]').setAttribute('aria-label', this.cards[1 - this.active].dataset.showLabel);
      });
      const target = this.cards[1 - this.active];
      this.querySelector('[data-hbn-swap]').setAttribute('aria-controls', target.id);
      this.querySelector('[data-hbn-deck-status]').textContent = `0${this.active + 1} / 02 — ${this.cards[this.active].dataset.cardLabel}`;
    }
    async swap() {
      if (this.busy) return;
      const focusedCard = this.cards.find(card => card.contains(document.activeElement));
      const previous = this.active;
      const start = this.cards.map(card => getComputedStyle(card).transform);
      this.busy = true;
      this.classList.add('is-switching');
      this.active = 1 - previous;
      this.update();
      const finish = this.cards.map(card => getComputedStyle(card).transform);
      if (!reduced.matches && this.cards[0].animate) {
        this.animations = this.cards.map((card, i) => {
          const incoming = i !== previous;
          return card.animate([
            { transform: start[i], zIndex: incoming ? 1 : 3, offset: 0 },
            { transform: `translate(${incoming ? '17%' : '-17%'}, -26px) rotate(${incoming ? '7' : '-7'}deg) scale(.98)`, zIndex: incoming ? 3 : 1, offset: .5 },
            { transform: finish[i], zIndex: incoming ? 3 : 1, offset: 1 }
          ], { duration: 680, easing: 'cubic-bezier(.3,0,.2,1)' });
        });
        await Promise.allSettled(this.animations.map(a => a.finished));
      }
      if (!this.isConnected) return;
      this.animations = []; this.busy = false; this.classList.remove('is-switching');
      if (focusedCard && focusedCard.contains(document.activeElement)) this.cards[this.active].querySelector('[data-hbn-card-trigger]').focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.animations?.forEach(a => a.cancel()); this.animations = [];
      this.classList.remove('is-ready', 'is-switching');
    }
  }
  if (!customElements.get('hbn-preview-deck')) customElements.define('hbn-preview-deck', PreviewDeck);
  const mount = scope => {
    const roots = [...scope.querySelectorAll('[data-hbn-magic]')];
    if (scope.matches?.('[data-hbn-magic]')) roots.unshift(scope);
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, new MagicSurface(root)); });
  };
  window.HbnMagicHomepage = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((controller, root) => {
      if (event.target === root || event.target.contains(root)) { controller.destroy(); instances.delete(root); }
    });
  });
})();
